import { Box } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { OnChangeFn, Row, SortingState } from "@tanstack/react-table"
import { getCoreRowModel } from "@tanstack/react-table"
import type { FC } from "react"
import React, { useCallback, useEffect, useMemo, useRef } from "react"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import { BASE_SORTING_TABLE_FEATURE_PROPS } from "@/components/Tables/Aeros/shared"
import type { CompanyListCertification, CompanyListCertificationSortField } from "@/graphql/codegen/graphql"
import { CompanyStatus, SortingOrder } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import { queryClient } from "@/providers/GraphqlRouterProvider"
import { NotificationType } from "@/schemas/notification"
import {
  type SortableAccessorKey,
  accessorKeyToCompanyListCertificationSortField,
  companyListCertificationSortFieldToAccessoryKey,
} from "@/screens/Companies/components/tables/CetrificateExpirationsTableUtils"
import useCertificateExpirationsTableColumns from "@/screens/Companies/hooks/useCertificateExpirationsTableColumns"
import type { CertificationExpirationsSearchFilters } from "@/screens/Companies/stores/useCertificationExpirationsSearchStore"
import { useCertificationExpirationsSearchStore } from "@/screens/Companies/stores/useCertificationExpirationsSearchStore"
import { SearchCategory } from "@/screens/Companies/types/enums"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const CertificateExpirationsTable: FC = () => {
  const navigate = useNavigate()
  const currentSearch = useSearch({ from: "/app/companies/" }) as CertificationExpirationsSearchFilters
  const { query, field, order, category } = currentSearch

  const data = useCertificationExpirationsSearchStore(({ data }) => data)
  const fetching = useCertificationExpirationsSearchStore(({ fetching }) => fetching)
  const error = useCertificationExpirationsSearchStore(({ error }) => error)
  const executeSearch = useCertificationExpirationsSearchStore(({ executeSearch }) => executeSearch)
  const loadMore = useCertificationExpirationsSearchStore(({ loadMore }) => loadMore)
  const clearStore = useCertificationExpirationsSearchStore(({ clearStore }) => clearStore)
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const columns = useCertificateExpirationsTableColumns()

  const searchFilters: CertificationExpirationsSearchFilters = useMemo(() => {
    const { types, ...otherSearchParams } = currentSearch
    return {
      companyTypes: types,
      ...otherSearchParams,
    }
  }, [currentSearch])

  const [throttledLoadMore] = useThrottle(() => loadMore(queryClient, searchFilters), 200)
  const onScroll = useScrollLoadMore({ fetching, loadMore: throttledLoadMore, scrollContainerRef: tableContainerRef })

  useEffect(() => {
    navigate({
      search: {
        statuses: [CompanyStatus.Active],
        field,
        category: category as SearchCategory,
        order,
      },
      to: "/app/companies",
      replace: true,
    })
  }, [field, navigate, order, category])

  useEffect(() => {
    executeSearch(queryClient, searchFilters)
  }, [executeSearch, searchFilters])

  const sorting = useMemo(() => {
    const tableSortField = companyListCertificationSortFieldToAccessoryKey.get(
      field as CompanyListCertificationSortField
    ) as string
    const tableSortOrder = order || SortingOrder.Asc
    return [
      {
        id: tableSortField,
        asc: tableSortOrder === SortingOrder.Asc,
        desc: tableSortOrder === SortingOrder.Desc,
      },
    ]
  }, [field, order])

  const handleSortingChange: OnChangeFn<SortingState> = (updaterFn) => {
    // @ts-expect-error - updaterFn is know fn
    const newSorting = updaterFn(sorting)

    if (newSorting.length > 0) {
      const sort = newSorting[0]
      const key = sort.id as SortableAccessorKey
      const newField = accessorKeyToCompanyListCertificationSortField.get(key)

      const params = {
        query,
        category: SearchCategory.CERTIFICATION,
        field: newField,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }

      navigate({
        search: (prev) => ({
          ...prev,
          ...params,
        }),
        to: "/app/companies",
        replace: true,
      })
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  const handleRowClick = useCallback(
    (row: Row<CompanyListCertification>) => {
      if (!row.original?.companyId) {
        notify({
          message: "Failed to go to company certificates",
          type: NotificationType.Error,
        })
        return
      }
      navigate({ to: `/app/companies/${row.original.companyId}/edit/certification` })
    },
    [navigate, notify]
  )

  return (
    <Box className='flex h-full flex-col'>
      <AerosBaseTable<CompanyListCertification>
        error={error}
        features={{
          table: {
            ...BASE_SORTING_TABLE_FEATURE_PROPS.table,
          },
          tanstackOptions: {
            ...BASE_SORTING_TABLE_FEATURE_PROPS.tanstackOptions,
            data,
            columns,
            state: {
              sorting,
              isLoading: fetching,
            },
            getCoreRowModel: getCoreRowModel(),
            onSortingChange: handleSortingChange,
          },
        }}
        slotProps={{
          container: {
            ref: tableContainerRef,
            onScroll,
          },
          bodyRow: {
            onClick: (row) => {
              if ("original" in row) {
                handleRowClick(row)
              }
            },
            className: "hover:bg-primary-100 cursor-pointer",
          },
        }}
        noDataMessage='No companies found'
      />
    </Box>
  )
}

export default CertificateExpirationsTable
